import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./LockedFeature.css";
import LockedFeatureImg from "../../../assets/empty-states/LockedFeatureImg.svg";

/**
 * LockedFeature Component
 *
 * Displays a locked feature state / upsell page for capabilities exclusive
 * to higher subscription plans. Can be used either as an embedded state
 * (similar to EmptyState in tables/views) or as a standalone full page.
 *
 * @param {string} [title] - Custom title (defaults to "Unlock more with salesroute.ai")
 * @param {string} [description] - Custom description (defaults to plan upgrade prompt)
 * @param {string} [subText] - Alias for description
 * @param {string} [buttonText] - Custom button text (defaults to "View Plans & Upgrade")
 * @param {Function} [onButtonClick] - Custom action on button click (defaults to navigate('/plans'))
 * @param {Function} [onUpgradeClick] - Alias for onButtonClick
 * @param {string} [image] - Custom image/illustration URL
 * @param {string} [featureName] - Optional feature name
 * @param {boolean} [fullPage=false] - When true, expands container to fill page height
 * @param {string} [className=""] - Additional CSS class names
 */
const LockedFeature = ({
  title,
  description,
  subText,
  buttonText,
  onButtonClick,
  onUpgradeClick,
  image,
  featureName,
  fullPage = false,
  className = "",
  ...props
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleUpgrade = onButtonClick || onUpgradeClick || (() => navigate("/plans"));

  const displayTitle = title || t("Unlock more with salesroute.ai");
  const displayDescription =
    description ||
    subText ||
    t(
      "This feature isn't included in your current plan. Upgrade to unlock this capability and get more from your sales operations."
    );
  const displayButtonText = buttonText || t("View Plans & Upgrade");
  const displayImage = image || LockedFeatureImg;

  return (
    <div
      className={`locked-feature-container ${fullPage ? "full-page" : ""} ${className}`}
      {...props}
    >
      <div className="locked-feature-image-wrapper">
        <img
          src={displayImage}
          alt={t("Locked Feature")}
          className="locked-feature-image"
        />
      </div>

      <h3 className="locked-feature-heading">{t(displayTitle)}</h3>

      <p className="locked-feature-subtext">{t(displayDescription)}</p>

      {displayButtonText && (
        <button
          type="button"
          onClick={handleUpgrade}
          className="locked-feature-button"
        >
          {t(displayButtonText)}
        </button>
      )}
    </div>
  );
};

export default LockedFeature;
